/**
 * App store — Zustand store for global client-side state.
 * Replaces LanguageReducer, CopyPasteReducer, DisplayHelpersReducer.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface AppState {
  /** UI language code, e.g. 'ru', 'en' */
  language: string;
  /** Sidebar collapsed state */
  sidebarCollapsed: boolean;
  /** Theme preference */
  theme: Theme;

  setLanguage: (language: string) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: Theme) => void;
}

/** Apply or remove the `dark` class on <html> */
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // system
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  }
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      language: 'ru',
      sidebarCollapsed: false,
      theme: 'light' as Theme,

      setLanguage: (language) => set({ language }),
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: 'ep-app-store',
      partialize: (state) => ({
        language: state.language,
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply saved theme on app load
        if (state?.theme) applyTheme(state.theme);
      },
    },
  ),
);
